/**
 * GameStatus enum - represents the user's progress status for a game
 * Requirements: 6.4
 */
export const GameStatus = Object.freeze({
    ABANDONED: "Abandoned",
    NOT_STARTED: "Not played",
    DIDNT_FINISH: "Shelved",
    WANT_TO_PLAY: "Want to play",
    PLAYING: "Playing",
    COMPLETED: "Completed"
});

/**
 * GameEntry class - represents a tracked videogame with Steam metadata and user tracking data
 * Requirements: 3.1, 6.1, 6.2, 6.3, 6.5
 */
export class GameEntry {
    /**
     * Creates a new GameEntry
     * @param {Object} data - Game entry data
     * @param {string} data.steamID - Steam unique identifier
     * @param {string} data.title - Game title
     * @param {string} data.imageURL - URL to game image
     * @param {string[]} data.genres - Array of genre tags
     * @param {string} data.storeURL - Steam store page URL
     * @param {string} data.creator - Game creator/developer name
     * @param {Date} data.releaseDate - Game release date
     * @param {boolean} data.isDLC - Whether this is DLC or a main game
     * @param {Date|null} [data.lastPlayed=null] - Date when user last played the game
     * @param {string|null} [data.status=null] - User's progress status (GameStatus value)
     * @param {number|null} [data.rating=null] - User's rating (1-5)
     */
    constructor(data) {
        // API-provided fields (required)
        this.steamID = data.steamID;
        this.source = data.source || 'steam';
        this.title = data.title;
        this.imageURL = data.imageURL;
        this.genres = data.genres;
        this.storeURL = data.storeURL;
        this.creator = data.creator;
        this.releaseDate = data.releaseDate;
        this.isDLC = data.isDLC;

        // User-provided fields (optional)
        this.lastPlayed = data.lastPlayed !== undefined ? data.lastPlayed : null;
        this.status = data.status !== undefined ? data.status : null;
        this.rating = data.rating !== undefined ? data.rating : null;
    }
}

/**
 * UserMetadata class - represents user-provided tracking information for a game
 * Requirements: 6.1, 6.2, 6.3, 6.5
 */
export class UserMetadata {
    /**
     * Creates a new UserMetadata object
     * @param {Object} [data={}] - User metadata
     * @param {Date} [data.lastPlayed] - Date when user last played the game
     * @param {string} [data.status] - User's progress status (GameStatus value)
     * @param {number} [data.rating] - User's rating (1-5)
     */
    constructor(data = {}) {
        this.lastPlayed = data.lastPlayed;
        this.status = data.status;
        this.rating = data.rating;
    }
}
